import { FileOutput } from 'dtos/diligence/FileOutput';
import { UploadOutput } from 'dtos/diligence/UploadOutput';
import { makeAutoObservable } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { serverUri } from 'utils/config';

const uploadFieldName = 'file';

export class UploadStore {
  percent: number = 0;

  constructor(
    private readonly token: string,
    readonly files: File[],
    readonly additionalParams: { [key: string]: string },
    private readonly startPercentAfterUpload: number,
    private readonly approxBytesPerSecAfterUpload: number,
    private readonly authStore: AuthStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setPercent(percent: number) {
    this.percent = percent;
  }

  async upload(): Promise<FileOutput[]> {
    const totalSizeBytes = this.files.reduce((prev, current) => prev + current.size, 0);
    this.setPercent(0);
    return new Promise((resolve, reject) => {
      let loadStartTime: number | undefined = undefined;
      let progressInterval: number | undefined = undefined;

      const waitProgress = () => {
        if (loadStartTime === undefined) return;
        const now = new Date().getTime();
        const elaspedMs = now - loadStartTime;
        const approxPercentDone = (this.approxBytesPerSecAfterUpload * (elaspedMs / 1000)) / totalSizeBytes;
        const displayPercent = Math.min(
          99,
          Math.floor(this.startPercentAfterUpload + approxPercentDone * (100 - this.startPercentAfterUpload))
        );
        this.setPercent(displayPercent);
      };

      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener?.(
        // not implemented in msw
        'progress',
        (event) => {
          if (event.lengthComputable) {
            this.setPercent(Math.round((event.loaded * this.startPercentAfterUpload) / event.total));
          }
        },
        false
      );

      xhr.upload.addEventListener?.(
        // not implemented in msw
        'load',
        (event) => {
          this.setPercent(this.startPercentAfterUpload);
          loadStartTime = new Date().getTime();
          progressInterval = window.setInterval(waitProgress, 100);
        },
        false
      );

      xhr.addEventListener('loadend', (event) => {
        if (progressInterval !== undefined) {
          window.clearInterval(progressInterval);
          progressInterval = undefined;
        }
        if (xhr.readyState === 4) {
          this.setPercent(100);
          if (xhr.status >= 200 && xhr.status < 300) {
            const response: UploadOutput = JSON.parse(xhr.responseText);
            resolve(response.files);
          } else {
            reject();
          }
        } else {
          reject();
        }
      });

      const url = new URL(`${serverUri}/diligence/${encodeURIComponent(this.token)}/files`);
      const params = new URLSearchParams();
      for (const key in this.additionalParams) {
        params.set(key, this.additionalParams[key]);
      }
      url.search = params.toString();

      xhr.open('POST', url.href);
      if (this.authStore.authHeader) {
        xhr.setRequestHeader('Authorization', this.authStore.authHeader);
      }

      const formData = new FormData();
      this.files.forEach((file) => {
        formData.append(uploadFieldName, file);
      });
      xhr.send(formData);
    });
  }
}
